/**
 * @fileoverview Institution Approval Stepper component
 * Shows the multi-step approval workflow for institutions
 */

import React from "react";
import PropTypes from "prop-types";
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Alert,
    Paper,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    Description as DocumentIcon,
    HourglassEmpty as WaitingIcon,
    Gavel as RegulatoryIcon,
    Business as DirectorIcon,
    CheckCircle as CompleteIcon,
    Warning as WarningIcon
} from "@mui/icons-material";
import { intl } from "dmsbasereact";
import {
    InstitutionStatusEnum,
    CATEGORIES_REQUIRING_DIRECTOR_APPROVAL
} from "../../../constants/InstitutionStatus";

/**
 * Get the steps for institution approval based on categories
 * @param {number[]} categories - All institution categories
 * @returns {Array} Array of step objects
 */
const getSteps = (categories) => {
    const requiresDirector = categories.some(c => CATEGORIES_REQUIRING_DIRECTOR_APPROVAL.includes(c));

    const baseSteps = [
        {
            key: "registration",
            labelKey: "Institution.Step.Registration",
            defaultLabel: "Registration",
            descriptionKey: "Institution.Step.Registration.Desc",
            defaultDescription: "Institution data registered",
            icon: <DocumentIcon />,
        },
        {
            key: "gestao_lena",
            labelKey: "Institution.Step.GestaoLena",
            defaultLabel: "Gestão Lena",
            descriptionKey: "Institution.Step.GestaoLena.Desc",
            defaultDescription: "Initial analysis by Gestao Lena team",
            icon: <WaitingIcon />,
        },
        {
            key: "regulatorio",
            labelKey: "Institution.Step.Regulatorio",
            defaultLabel: "Regulatório",
            descriptionKey: "Institution.Step.Regulatorio.Desc",
            defaultDescription: "Validation by Regulatory team",
            icon: <RegulatoryIcon />,
        },
    ];

    if (requiresDirector) {
        baseSteps.push({
            key: "diretoria",
            labelKey: "Institution.Step.Diretoria",
            defaultLabel: "Diretoria",
            descriptionKey: "Institution.Step.Diretoria.Desc",
            defaultDescription: "Final approval by Directors",
            icon: <DirectorIcon />,
        });
    }

    baseSteps.push({
        key: "approved",
        labelKey: "Institution.Step.Approved",
        defaultLabel: "Approved",
        descriptionKey: "Institution.Step.Approved.Desc",
        defaultDescription: "Institution approved and active",
        icon: <CompleteIcon />,
    });

    return baseSteps;
};

/**
 * Get the active step index based on status
 * @param {number} status - Institution status code
 * @param {number[]} categories - All institution categories
 * @returns {number} Active step index
 */
const getActiveStep = (status, categories) => {
    const requiresDirector = categories.some(c => CATEGORIES_REQUIRING_DIRECTOR_APPROVAL.includes(c));
    const totalSteps = requiresDirector ? 5 : 4; // Total number of steps

    switch (status) {
        case InstitutionStatusEnum.PENDING_APPROVAL:
        case InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA:
            return 1; // Gestão Lena step
        case InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO:
            return 2; // Regulatório step
        case InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA:
            return requiresDirector ? 3 : 2; // Diretoria step (only if applicable)
        case InstitutionStatusEnum.APPROVED:
            return totalSteps; // Beyond last step = all steps completed
        case InstitutionStatusEnum.REJECTED:
            return 1; // Back to first approval step
        default:
            return 0; // Registration
    }
};

/**
 * Get status info message
 * @param {number} status - Institution status code
 * @param {string} justification - Rejection justification
 * @returns {Object} Status info with message and severity
 */
const getStatusInfo = (status, justification) => {
    switch (status) {
        case InstitutionStatusEnum.PENDING_APPROVAL:
        case InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA:
            return {
                severity: "info",
                message: intl.get("Institution.Status.PendingGestaoLena.Simple")
                    .d("Awaiting analysis by the Gestao Lena team."),
            };
        case InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO:
            return {
                severity: "info",
                message: intl.get("Institution.Status.PendingRegulatorio.Simple")
                    .d("Awaiting validation by the Regulatory team."),
            };
        case InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA:
            return {
                severity: "info",
                message: intl.get("Institution.Status.PendingDiretoria.Simple")
                    .d("Awaiting final approval by Directors."),
            };
        case InstitutionStatusEnum.APPROVED:
            return {
                severity: "success",
                message: intl.get("Institution.Status.Approved.Message")
                    .d("Your institution is approved and all features are available."),
            };
        case InstitutionStatusEnum.REJECTED:
            return {
                severity: "warning",
                message: justification
                    ? intl.get("Institution.Status.Rejected.MessageWithReason", { justification })
                        .d("Your institution has been rejected. Reason: {justification}. Fix the issues and resubmit for analysis.")
                    : intl.get("Institution.Status.Rejected.Message")
                        .d("Your institution has been rejected. Fix the indicated issues and resubmit for a new analysis."),
            };
        case InstitutionStatusEnum.INACTIVE:
            return {
                severity: "error",
                message: intl.get("Institution.Status.Inactive.Message")
                    .d("Your institution is inactive. Contact support for more information."),
            };
        default:
            return {
                severity: "info",
                message: intl.get("Institution.Status.Registration.Message")
                    .d("Complete your institution registration to start the approval process."),
            };
    }
};

/**
 * Get step status label (Approved, Pending, etc.)
 * @param {number} index - Step index
 * @param {number} activeStep - Current active step
 * @param {boolean} isRejected - Whether institution is rejected
 * @returns {Object|null} Label info with text and color
 */
const getStepStatusLabel = (index, activeStep, isRejected) => {
    const isCompleted = index < activeStep;
    const isActive = index === activeStep;

    if (isCompleted) {
        return {
            text: intl.get("Approved").d("Approved"),
            color: "success.main"
        };
    }
    if (isActive && isRejected) {
        return {
            text: intl.get("Rejected").d("Rejected"),
            color: "warning.main"
        };
    }
    if (isActive) {
        return {
            text: intl.get("Pending").d("Pending"),
            color: "primary.main"
        };
    }
    return null;
};

/**
 * InstitutionApprovalStepper - Shows the multi-step approval workflow
 */
export const InstitutionApprovalStepper = ({
    status,
    categories = [],
    statusJustification = null,
    showAlert = true,
    compact = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const steps = getSteps(categories);
    const activeStep = getActiveStep(status, categories);
    const statusInfo = getStatusInfo(status, statusJustification);
    const isRejected = status === InstitutionStatusEnum.REJECTED;
    const isApproved = status === InstitutionStatusEnum.APPROVED;

    const getStepIcon = (index, step) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;

        if (isCompleted) {
            return <CompleteIcon />;
        }
        if (isRejected && isActive) {
            return <WarningIcon />;
        }
        return step.icon;
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: compact ? 2 : 3,
                mb: 2,
                bgcolor: isApproved ? "success.50" : isRejected ? "warning.50" : "background.paper",
                border: 1,
                borderColor: isApproved ? "success.200" : isRejected ? "warning.200" : "divider",
                borderRadius: 2
            }}
        >
            {/* Header */}
            <Box sx={{ mb: compact ? 2 : 3 }}>
                <Typography
                    variant={compact ? "subtitle1" : "h6"}
                    sx={{ fontWeight: 600, mb: 0.5 }}
                >
                    {intl.get("Institution.ApprovalWorkflow").d("Approval Workflow")}
                </Typography>
                {!compact && (
                    <Typography variant="body2" color="text.secondary">
                        {intl.get("Institution.ApprovalWorkflow.Description")
                            .d("Track your institution's approval status")}
                    </Typography>
                )}
            </Box>

            {/* Stepper */}
            <Stepper
                activeStep={activeStep}
                alternativeLabel={!isMobile}
                orientation={isMobile ? "vertical" : "horizontal"}
                sx={{ mb: showAlert ? 3 : 0 }}
            >
                {steps.map((step, index) => {
                    const isCompleted = index < activeStep;
                    const isActive = index === activeStep;
                    const stepStatusLabel = getStepStatusLabel(index, activeStep, isRejected);

                    return (
                        <Step
                            key={step.key}
                            completed={isCompleted}
                        >
                            <StepLabel
                                error={isRejected && isActive}
                                StepIconComponent={() => (
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: isCompleted
                                                ? "success.main"
                                                : isActive
                                                    ? isRejected
                                                        ? "warning.main"
                                                        : "primary.main"
                                                    : "grey.300",
                                            color: isCompleted || isActive ? "white" : "grey.600",
                                            "& svg": { fontSize: 18 }
                                        }}
                                    >
                                        {getStepIcon(index, step)}
                                    </Box>
                                )}
                                optional={
                                    stepStatusLabel ? (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: stepStatusLabel.color,
                                                fontWeight: 500
                                            }}
                                        >
                                            {stepStatusLabel.text}
                                        </Typography>
                                    ) : null
                                }
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: isActive ? 600 : 400,
                                        color: isActive
                                            ? isRejected
                                                ? "warning.main"
                                                : "primary.main"
                                            : isCompleted
                                                ? "success.main"
                                                : "text.secondary"
                                    }}
                                >
                                    {intl.get(step.labelKey).d(step.defaultLabel)}
                                </Typography>
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {/* Status Alert */}
            {showAlert && statusInfo.message && (
                <Alert
                    severity={statusInfo.severity}
                    icon={isRejected ? <WarningIcon /> : undefined}
                    sx={{
                        "& .MuiAlert-message": { width: "100%" }
                    }}
                >
                    <Typography variant="body2">
                        {statusInfo.message}
                    </Typography>
                </Alert>
            )}
        </Paper>
    );
};

InstitutionApprovalStepper.propTypes = {
    status: PropTypes.number.isRequired,
    categories: PropTypes.arrayOf(PropTypes.number),
    statusJustification: PropTypes.string,
    showAlert: PropTypes.bool,
    compact: PropTypes.bool,
};

